import { ChevronLeft, ChevronRight, LayoutGrid, List, Lightbulb, Send, Settings, Upload } from "lucide-react";

const CHIPS = [
    "Where did my money go this month?",
    "Which category spiked compared to last month?",
    "Who are my top 3 merchants this month?",
];

const NAV_ITEMS = [
    { icon: LayoutGrid, label: "Home", active: true },
    { icon: List, label: "Txns" },
    { icon: Upload, label: "Import" },
    { icon: Lightbulb, label: "Insights" },
    { icon: Settings, label: "Settings" },
];

const BARS = [
    { m: "May", v: 38 },
    { m: "Jun", v: 55 },
    { m: "Jul", v: 30 },
    { m: "Aug", v: 62 },
    { m: "Sep", v: 48 },
    { m: "Oct", v: 72 },
];

function Chassis({ children, testid }) {
    return (
        <div
            data-testid={testid}
            className="relative mx-auto w-[290px] rounded-[44px] border border-white/10 bg-[#161618] p-2.5 shadow-[0_40px_90px_-24px_rgba(0,0,0,0.9)] sm:w-[320px]"
        >
            <div className="relative select-none overflow-hidden rounded-[36px] bg-fynary-bg">
                <div className="absolute left-1/2 top-2.5 z-20 h-[18px] w-24 -translate-x-1/2 rounded-full bg-[#161618]" />
                {children}
            </div>
        </div>
    );
}

function StatCard({ label, value, valueClass, sub }) {
    return (
        <div className="rounded-2xl border border-white/10 bg-fynary-surface p-3">
            <p className="text-[11px] text-fynary-muted">{label}</p>
            <p className={`mt-0.5 text-base font-bold ${valueClass}`}>{value}</p>
            {sub ? <p className="text-[10px] text-fynary-muted">{sub}</p> : null}
        </div>
    );
}

function DashboardScreen() {
    return (
        <div className="pb-2 pt-3">
            <div className="flex items-center justify-between px-4 pt-4">
                <div>
                    <p className="font-display text-xl font-extrabold lowercase tracking-tight text-fynary-lime">fynary.</p>
                    <p className="text-[11px] text-fynary-muted">Hi there</p>
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-fynary-lime px-3.5 py-2 text-[11px] font-semibold text-fynary-bg">
                    ? Ask fynary
                </span>
            </div>

            <div className="mt-4 flex items-center justify-between px-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-fynary-surface">
                    <ChevronLeft className="h-4 w-4 text-white" />
                </span>
                <p className="text-sm font-semibold">October 2026</p>
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-fynary-surface">
                    <ChevronRight className="h-4 w-4 text-white" />
                </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 px-4">
                <StatCard label="Spent" value="₹12,480" valueClass="text-white" />
                <StatCard label="Income" value="₹45,000" valueClass="text-fynary-lime" />
                <StatCard label="Savings" value="₹32,520" valueClass="text-fynary-lime" sub="72% rate" />
                <StatCard label="Largest" value="₹6,200" valueClass="text-white" sub="Avg ₹940" />
            </div>

            <div className="mx-4 mt-2 rounded-2xl border border-white/10 bg-fynary-surface p-4">
                <p className="text-sm font-semibold">Last 6 Months</p>
                <div className="mt-3 flex h-24 items-end justify-between gap-2">
                    {BARS.map((b) => (
                        <div key={b.m} className="flex flex-1 flex-col items-center gap-1.5">
                            <div
                                className="w-full rounded-md bg-fynary-lime/90"
                                style={{ height: `${(b.v / 72) * 72}px` }}
                            />
                            <span className="text-[8px] text-fynary-muted">{b.m}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-white/10 px-4 py-2.5">
                {NAV_ITEMS.map((n) => (
                    <div
                        key={n.label}
                        className={`flex flex-col items-center gap-0.5 ${n.active ? "text-fynary-lime" : "text-fynary-muted"}`}
                    >
                        <n.icon className="h-[18px] w-[18px]" />
                        <span className="text-[8px]">{n.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AssistantScreen() {
    return (
        <div className="pb-2 pt-3">
            <div className="flex items-center gap-3 px-4 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-fynary-surface">
                    <ChevronLeft className="h-4 w-4 text-white" />
                </span>
                <div>
                    <p className="text-sm font-bold">Spending Assistant</p>
                    <p className="text-[10px] text-fynary-muted">Ask about your money</p>
                </div>
            </div>

            <div className="mx-4 mt-4 rounded-2xl border border-white/10 bg-fynary-surface p-4">
                <p className="font-display text-2xl font-extrabold text-fynary-lime">?</p>
                <p className="mt-2 text-sm font-bold">Hi, I'm fynary's assistant</p>
                <p className="mt-1 text-[11px] leading-relaxed text-fynary-muted">
                    I only use your own data to answer. Try one of these:
                </p>
            </div>

            <div className="mt-2 flex flex-col gap-2 px-4">
                {CHIPS.map((c) => (
                    <div key={c} className="rounded-xl border border-white/10 bg-fynary-surface px-3.5 py-3 text-[11px] leading-snug">
                        {c}
                    </div>
                ))}
            </div>

            <div className="mt-4 flex items-center gap-2 px-4 pb-4">
                <div className="flex-1 rounded-full border border-white/10 bg-fynary-surface px-4 py-2.5 text-[11px] text-fynary-muted">
                    Ask about your spending…
                </div>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-fynary-lime">
                    <Send className="h-4 w-4 text-fynary-bg" />
                </span>
            </div>
        </div>
    );
}

export default function PhoneMockup({ screen = "dashboard", testid }) {
    return (
        <Chassis testid={testid}>
            {screen === "dashboard" ? <DashboardScreen /> : <AssistantScreen />}
        </Chassis>
    );
}
